import {
    type CreateIndicatorInput,
    IndicatorType,
} from "@generated/client/graphql";
import Papa from "papaparse";
import { useMemo } from "react";
import { useThemesAndSubtheme, useUnits } from "./data";

export type CSVRow = {
    name: string;
    uname: string;
    description: string;
    cmsUnitId: string;
    cmsThemeId: string;
    cmsSubthemeId: string;
};

const EXPECTED_HEADERS = [
    "name",
    "uname",
    "description",
    "unit-bv-id",
    "theme-bv-id",
    "subtheme-bv-id",
] as const;

type ExpectedHeader = (typeof EXPECTED_HEADERS)[number];

const validateCSVHeaders = (
    headers: string[],
): { isValid: boolean; missingHeaders: string[]; extraHeaders: string[] } => {
    const headerSet = new Set(headers);
    const expectedHeaderSet = new Set(EXPECTED_HEADERS);

    const missingHeaders = EXPECTED_HEADERS.filter(
        (header) => !headerSet.has(header),
    );
    const extraHeaders = headers.filter(
        (header) => !expectedHeaderSet.has(header as ExpectedHeader),
    );

    return {
        isValid: missingHeaders.length === 0,
        missingHeaders,
        extraHeaders,
    };
};

export const useValidateCSVRows = (csvRows: CSVRow[]) => {
    const { themes, subthemes } = useThemesAndSubtheme();
    const units = useUnits();

    const validateRow = (csvRow: CSVRow) => {
        const theme = themes?.find(
            (theme) => theme.cmsThemeId === csvRow.cmsThemeId,
        );
        const subtheme = subthemes?.find(
            (subtheme) => subtheme.cmsSubThemeId === csvRow.cmsSubthemeId,
        );
        const unit = units?.find((unit) => unit.bvId === csvRow.cmsUnitId);

        const isValid =
            !!csvRow.name && !!csvRow.uname && !!unit && !!theme && !!subtheme;
        return {
            csvRow,
            theme,
            subtheme,
            unit,
            isValid,
            input: isValid
                ? ({
                      name: csvRow.name,
                      description: csvRow.description,
                      cmsUnitId: csvRow.cmsUnitId,
                      uname: csvRow.uname,
                      themeId: theme?.id,
                      subthemeId: subtheme?.id,
                      type: IndicatorType.Reporting,
                  } as CreateIndicatorInput)
                : undefined,
        };
    };

    const validatedRows = useMemo(
        () => csvRows.map(validateRow),
        [csvRows, themes, subthemes],
    );

    return validatedRows;
};

export const useCsvImport = (onAddIndicators: (inputs: CSVRow[]) => void) => {
    const onCsvImport = (file: File): Promise<void> => {
        return new Promise((resolve, reject) => {
            Papa.parse(file as unknown as string, {
                header: true,
                skipEmptyLines: true,
                complete: function (
                    results: Papa.ParseResult<Record<string, string>>,
                ) {
                    try {
                        const headers = results.meta.fields || [];
                        const headerValidation = validateCSVHeaders(headers);

                        if (!headerValidation.isValid) {
                            const errorMessage = `Invalid CSV format. ${
                                headerValidation.missingHeaders.length > 0
                                    ? `Missing required headers: ${headerValidation.missingHeaders.join(", ")}. `
                                    : ""
                            }${
                                headerValidation.extraHeaders.length > 0
                                    ? `Unexpected headers: ${headerValidation.extraHeaders.join(", ")}. `
                                    : ""
                            } Expected headers: ${EXPECTED_HEADERS.join(", ")}`;

                            reject(new Error(errorMessage));
                            return;
                        }

                        const csvInputs = results.data.map(
                            (row: Record<string, string>) => {
                                const csvRow: CSVRow = {
                                    name: row.name || "",
                                    uname: row.uname || "",
                                    cmsUnitId: row["unit-bv-id"] || "",
                                    cmsThemeId: row["theme-bv-id"] || "",
                                    cmsSubthemeId: row["subtheme-bv-id"] || "",
                                    description: row.description || "",
                                };
                                return csvRow;
                            },
                        );
                        onAddIndicators(csvInputs);
                        resolve();
                    } catch (error) {
                        reject(error);
                    }
                },
            });
        });
    };

    return onCsvImport;
};
